import { useEffect, useState } from "react";

import { Button } from "../../../Components/Buttons";
import { FormInput, FormSection, FormSelect } from "../../../Components/Form";
import { ToastMessage } from "../../../Components/Utils";
import { useFetchLatestContactSettingQuery, useCreateContactSettingMutation } from "../../../stores/api/contactSettings.api";

const EMPTY = {
  email: "",
  phone: "",
  address: "",
  opening: {
    weekdays: { range: "Mon-Thu", time: { start: "", end: "", closed: false } },
    friday: { start: "", end: "", closed: false },
    saturday: { start: "", end: "", closed: false },
    sunday: { start: "", end: "", closed: false },
  },
};

const ContactSettings = () => {
  const [formData, setFormData] = useState(EMPTY);
  const { data: fetched, isLoading } = useFetchLatestContactSettingQuery();
  const [saveSettings, { isLoading: isSaving }] = useCreateContactSettingMutation();

  useEffect(() => {
    if (fetched) {
      setFormData({
        email: fetched.email || "",
        phone: fetched.phone || "",
        address: fetched.address || "",
        opening: {
          weekdays: fetched.opening?.weekdays || { range: "Mon-Thu", time: { start: "", end: "", closed: false } },
          friday: fetched.opening?.friday || { start: "", end: "", closed: false },
          saturday: fetched.opening?.saturday || { start: "", end: "", closed: false },
          sunday: fetched.opening?.sunday || { start: "", end: "", closed: false },
        },
      });
    }
  }, [fetched]);

  const setField = (name, value) => {
    setFormData((prev) => {
      if (!name.includes(".")) return { ...prev, [name]: value };
      const parts = name.split(".");
      const out = { ...prev };
      let cur = out;
      for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        cur[p] = { ...(cur[p] || {}) };
        cur = cur[p];
      }
      cur[parts[parts.length - 1]] = value;
      return out;
    });
  };

  const includesInWeekRange = (range, day) => {
    if (!range) return false;
    const r = range.toLowerCase();
    if (r === "mon-thu") return false;
    if (r === "mon-fri") return day === "friday";
    if (r === "mon-sat") return day === "friday" || day === "saturday";
    if (r === "mon-sun") return day === "friday" || day === "saturday" || day === "sunday";
    return false;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      const opening = { ...(payload.opening || {}) };
      const wtime = opening.weekdays?.time || {};
      opening.weekdays = { ...(opening.weekdays || {}), time: { ...wtime } };
      opening.weekdays.time.closed = !(opening.weekdays.time.start && opening.weekdays.time.end);
      ["friday", "saturday", "sunday"].forEach((d) => {
        if (includesInWeekRange(opening.weekdays?.range, d)) {
          // day covered by weekdays range -> copy weekdays time
          opening[d] = {
            start: opening.weekdays.time.start || null,
            end: opening.weekdays.time.end || null,
            closed: opening.weekdays.time.closed || false,
          };
        } else {
          const sec = opening[d] || {};
          sec.closed = !(sec.start && sec.end);
          opening[d] = sec;
        }
      });
      payload.opening = opening;

      await saveSettings(payload).unwrap();
      ToastMessage({ type: "success", title: "Success", message: "Contact settings saved!" });
    } catch (err) {
      ToastMessage({ type: "error", title: "Error!", message: err?.data?.message || err?.message || "Something went wrong" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mx-auto dark:bg-slate-900 rounded-lg p-6 space-y-5">
        <FormSection title="Contact Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Email" name="email" type="email" value={formData.email} required
              onChange={(val) => setField("email", val)}
              placeholder="contact@example.com"
            />
            <FormInput
              label="Phone" name="phone" value={formData.phone} required
              onChange={(val) => setField("phone", val)}
              placeholder="+1 (555) 000-0000"
            />
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <FormSelect
                  label="Weekdays Range"
                  value={formData.opening.weekdays?.range}
                  onChange={(val) => setField("opening.weekdays.range", val)}
                  options={[
                    { value: "Mon-Thu", label: "Mon - Thu" },
                    { value: "Mon-Fri", label: "Mon - Fri" },
                    { value: "Mon-Sat", label: "Mon - Sat" },
                    { value: "Mon-Sun", label: "Mon - Sun" },
                  ]}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <FormInput label="Weekdays Start" name="weekdays_start" type="time" value={formData.opening.weekdays?.time?.start}
                  onChange={(val) => setField("opening.weekdays.time.start", val)} />
                <FormInput label="Weekdays End" name="weekdays_end" type="time" value={formData.opening.weekdays?.time?.end}
                  onChange={(val) => setField("opening.weekdays.time.end", val)} />
              </div>

              {!includesInWeekRange(formData.opening.weekdays?.range, "friday") && (
                <>
                  <div>
                    <FormInput label="Friday Start" name="friday_start" type="time" value={formData.opening.friday?.start}
                      onChange={(val) => setField("opening.friday.start", val)} />
                  </div>
                  <div>
                    <FormInput label="Friday End" name="friday_end" type="time" value={formData.opening.friday?.end}
                      onChange={(val) => setField("opening.friday.end", val)} />
                  </div>
                </>
              )}

              {!includesInWeekRange(formData.opening.weekdays?.range, "saturday") && (
                <>
                  <div>
                    <FormInput label="Saturday Start" name="saturday_start" type="time" value={formData.opening.saturday?.start}
                      onChange={(val) => setField("opening.saturday.start", val)} />
                  </div>
                  <div>
                    <FormInput label="Saturday End" name="saturday_end" type="time" value={formData.opening.saturday?.end}
                      onChange={(val) => setField("opening.saturday.end", val)} />
                  </div>
                </>
              )}

              {!includesInWeekRange(formData.opening.weekdays?.range, "sunday") && (
                <>
                  <div>
                    <FormInput label="Sunday Start" name="sunday_start" type="time" value={formData.opening.sunday?.start}
                      onChange={(val) => setField("opening.sunday.start", val)} />
                  </div>
                  <div>
                    <FormInput label="Sunday End" name="sunday_end" type="time" value={formData.opening.sunday?.end}
                      onChange={(val) => setField("opening.sunday.end", val)} />
                  </div>
                </>
              )}
            </div>
            <FormInput
              label="Address" name="address" value={formData.address} required
              onChange={(val) => setField("address", val)}
              placeholder="123 Main St, City, Country" rows={3}
              className="md:col-span-2"
            />
          </div>
        </FormSection>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="submit" loading={isSaving} disabled={isSaving}
            className="!bg-orange-500 !text-white hover:!bg-orange-600"
            loadingText="Saving..."
          >
            Save Settings
          </Button>
        </div>
    </form>
  );
};

export default ContactSettings;
